import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, SITE_URL } from '../../../hooks/useSEO';

const HumanFocusedThreatsPage = () => {
  useSEO({
    title: 'Human-Focused Cyber Threats: Phishing & Social Engineering | Mortgage Moat',
    description: 'Learn how phishing, social engineering, BEC, and pretexting attacks target mortgage employees. Understand the threats and how to defend your financial firm.',
    keywords: 'human-focused cyber threats, phishing attacks, social engineering, business email compromise, mortgage security threats',
    canonical: '/knowledge-center/human-focused-threats',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Human-Focused Cyber Threats: Phishing & Social Engineering',
        description: 'Understanding social engineering attacks, phishing schemes, and manipulation tactics that target mortgage employees.',
        url: `${SITE_URL}/knowledge-center/human-focused-threats`,
        publisher: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        author: { '@type': 'Organization', name: 'Mortgage Moat' },
        dateModified: '2025-07-14',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Knowledge Center', item: `${SITE_URL}/knowledge-center/human-focused-threats` },
          { '@type': 'ListItem', position: 3, name: 'Human-Focused Threats', item: `${SITE_URL}/knowledge-center/human-focused-threats` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-red-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-shield-user-line"></i>
                Security Knowledge Center
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Human-Focused <span className="text-red-600">Cyber Threats</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Understanding social engineering attacks, phishing schemes, and manipulation tactics that target your employees—the most critical vulnerability in your security infrastructure
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="primary" size="lg" className="whitespace-nowrap">
                  <i className="ri-shield-check-line mr-2"></i>
                  Get Protected Now
                </Button>
                <Button variant="outline" size="lg" className="whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Schedule Assessment
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-12">
                <div className="flex items-start gap-4">
                  <i className="ri-error-warning-line text-3xl text-red-600 flex-shrink-0"></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Critical Reality</h3>
                    <p className="text-gray-700">
                      <strong>95% of cybersecurity breaches</strong> involve human error. Your employees are the primary target for cybercriminals because technology can be bypassed through psychological manipulation. Understanding these threats is your first line of defense.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Human-Focused Threats Are So Dangerous</h2>
              <p className="text-lg text-gray-700 mb-6">
                While firewalls and antivirus software protect against technical attacks, human-focused threats exploit psychology, trust, and natural human behaviors. These attacks are increasingly sophisticated, personalized, and difficult to detect because they don't rely on breaking through technical defenses—they simply ask your employees to open the door.
              </p>
            </div>
          </div>
        </section>

        {/* Main Threat Types */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Major Human-Focused Threat Categories</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Recognize these attack methods to protect your organization from social engineering exploitation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Phishing Attacks */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-600">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-mail-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Phishing & Spear Phishing</h3>
                <p className="text-gray-700 mb-4">
                  Fraudulent emails designed to steal credentials, install malware, or trick employees into transferring funds. Spear phishing targets specific individuals with personalized information.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Email spoofing:</strong> Fake sender addresses appearing legitimate</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Urgent requests:</strong> Creating false emergencies to bypass judgment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Credential harvesting:</strong> Fake login pages stealing passwords</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Malicious attachments:</strong> Documents containing hidden malware</span>
                  </li>
                </ul>
              </div>

              {/* Business Email Compromise */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-orange-600">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-building-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Business Email Compromise (BEC)</h3>
                <p className="text-gray-700 mb-4">
                  Sophisticated scams where attackers impersonate executives or vendors to authorize fraudulent wire transfers or data disclosure.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>CEO fraud:</strong> Fake executive emails requesting urgent transfers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Vendor impersonation:</strong> Changing payment account details</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Account compromise:</strong> Hijacked legitimate email accounts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Average loss:</strong> $120,000 per successful BEC attack</span>
                  </li>
                </ul>
              </div>

              {/* Pretexting */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-600">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-voice-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Pretexting & Impersonation</h3>
                <p className="text-gray-700 mb-4">
                  Creating fabricated scenarios to manipulate victims into divulging confidential information or performing actions that compromise security.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>IT support scams:</strong> Fake tech support requesting access</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Authority exploitation:</strong> Impersonating regulators or auditors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Research gathering:</strong> Building trust through multiple contacts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Social media mining:</strong> Using public information for credibility</span>
                  </li>
                </ul>
              </div>

              {/* Baiting & Quid Pro Quo */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-blue-600">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-gift-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Baiting & Quid Pro Quo</h3>
                <p className="text-gray-700 mb-4">
                  Offering something enticing to lure victims into compromising situations, or promising services in exchange for information or access.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Infected USB drives:</strong> Leaving malware-loaded devices in parking lots</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Free software offers:</strong> Trojanized applications and downloads</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Prize notifications:</strong> Fake lottery or reward claims</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Service exchanges:</strong> Offering help in return for credentials</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Red Flags: How to Spot Human-Focused Attacks</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-alarm-warning-line text-red-600"></i>
                    Urgency & Pressure Tactics
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Demands for immediate action, threats of consequences, or artificial deadlines designed to prevent careful consideration
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-link-unlink-line text-red-600"></i>
                    Suspicious Links & Attachments
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Unexpected files, shortened URLs, misspelled domains, or requests to enable macros in documents
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-user-unfollow-line text-red-600"></i>
                    Unusual Sender Behavior
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Requests outside normal procedures, odd phrasing from known contacts, or communications from unfamiliar addresses
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-lock-unlock-line text-red-600"></i>
                    Requests for Sensitive Information
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Asking for passwords, financial data, or confidential information through email or phone without proper verification
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-gift-2-line text-red-600"></i>
                    Too Good to Be True Offers
                  </h4>
                  <p className="text-gray-700 text-sm">
                    Unexpected prizes, unrealistic promises, or offers that seem disproportionately generous or beneficial
                  </p>
                </div>

                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-600"></i>
                    Generic Greetings & Poor Quality
                  </h4>
                  <p className="text-gray-700 text-sm">
                    "Dear Customer" instead of your name, spelling errors, grammatical mistakes, or unprofessional formatting
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Protection Strategies */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Defending Against Human-Focused Threats</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Multi-layered protection strategies to minimize your organization's vulnerability
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-graduation-cap-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Security Awareness Training</h3>
                <p className="text-gray-700 mb-4">
                  Regular, engaging training programs that teach employees to recognize and respond to social engineering attempts
                </p>
                <ul className="text-left space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Monthly phishing simulations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Interactive scenario training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Real-world attack examples</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-shield-check-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Technical Controls</h3>
                <p className="text-gray-700 mb-4">
                  Email filtering, multi-factor authentication, and endpoint protection to reduce attack surface
                </p>
                <ul className="text-left space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Advanced email security</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>MFA on all accounts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>URL filtering & sandboxing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-file-list-3-line text-4xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Clear Policies & Procedures</h3>
                <p className="text-gray-700 mb-4">
                  Documented verification processes and reporting mechanisms that employees can easily follow
                </p>
                <ul className="text-left space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Verification protocols</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Incident reporting system</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>No-blame culture</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Protect Your Team from Human-Focused Threats
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
              Our comprehensive security awareness training and phishing simulation programs turn your employees from vulnerabilities into your strongest defense layer
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" size="lg" className="bg-white text-red-600 hover:bg-gray-100 border-white whitespace-nowrap">
                <i className="ri-mail-check-line mr-2"></i>
                Start Free Phishing Test
              </Button>
              <Button variant="primary" size="lg" className="bg-white text-red-600 hover:bg-gray-100 whitespace-nowrap">
                <i className="ri-calendar-line mr-2"></i>
                Schedule Training Demo
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HumanFocusedThreatsPage;
